import * as React from 'react';
import ReactDOM from 'react-dom';

import isElementInViewport from 'utils/isElementInViewport';
import ScrollWatch from 'components/ScrollWatch';
import EventTracking from 'lib/EventTracking';
import SettingsStore from 'stores/SettingsStore';
import { Dialog } from 'components/Dialog';

type Props = {
  children?: React.ReactNode;
  messageUuid: string;
  chatUuid: string | null | undefined;
  isBroadcast: boolean;
  canViewDeliveryInformation?: boolean;
};

class MessageViewTracker extends React.Component<Props> {
  static defaultProps = {
    isBroadcast: false,
  };

  componentDidMount() {
    document.addEventListener('visibilitychange', this.handleDocumentVisibilityChange);
    window.addEventListener('beforeunload', this.sendEventIfWasVisible);

    // To fix "Property 'on' does not exist on type 'ComponentType<any>'."
    Dialog.on('show', this.sendEventIfWasVisible);
    Dialog.on('hide', this.updateVisibilityTimestamp);
  }

  componentWillUnmount() {
    this.sendEventIfWasVisible();
    document.removeEventListener('visibilitychange', this.handleDocumentVisibilityChange);
    window.removeEventListener('beforeunload', this.sendEventIfWasVisible);
    Dialog.removeListener('show', this.sendEventIfWasVisible);
    Dialog.removeListener('hide', this.updateVisibilityTimestamp);
  }

  lastVisibleAt: Date | null | undefined;

  sendEventIfWasVisible = () => {
    if (!this.lastVisibleAt) {
      return;
    }

    const { messageUuid, chatUuid, isBroadcast, canViewDeliveryInformation } = this.props;
    const currentTime = new Date().getTime();
    // Extra null check to satisfy Flow
    const lastVisibleAtTime = this.lastVisibleAt ? this.lastVisibleAt.getTime() : currentTime;
    const duration = currentTime - lastVisibleAtTime;
    const minDuration = SettingsStore.getViewportTrackingMinTime();
    if (duration < minDuration) {
      return;
    }

    EventTracking.track('chats.message_viewed', {
      message_uuid: messageUuid,
      chat_uuid: chatUuid,
      duration,
      is_broadcast: isBroadcast,
      call_to_action: canViewDeliveryInformation ? 'delivery_summary' : null,
    });

    this.lastVisibleAt = null;
  };

  handleDocumentVisibilityChange = () => {
    if (document.hidden) {
      this.sendEventIfWasVisible();
    } else {
      this.updateVisibilityTimestamp();
    }
  };

  updateVisibilityTimestamp = () => {
    const element = ReactDOM.findDOMNode(this);
    if (!(element instanceof HTMLElement)) {
      throw new Error('MessageViewTracker findDOMNode returned non HTMLElement');
    }
    if (isElementInViewport(element)) {
      this.lastVisibleAt = new Date();
    }
  };

  render() {
    const child = React.Children.only(this.props.children);
    return (
      <ScrollWatch
        onEnter={() => {
          this.lastVisibleAt = new Date();
        }}
        onLeave={this.sendEventIfWasVisible}>
        {child}
      </ScrollWatch>
    );
  }
}

export default MessageViewTracker;
